import apiClient from '@/shared/api/client';
import type { ApiResponse } from '@/shared/api/types';
import type { MediaAsset, MediaPurpose } from '@/modules/campaigns/api/campaignsApi';

export type { MediaAsset, MediaPurpose };

export const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const;
export type AllowedImageMimeType = typeof ALLOWED_IMAGE_MIME_TYPES[number];

export interface RequestUploadResponse {
  asset: MediaAsset;
  uploadUrl: string;
  expiresInSeconds: number;
}

const base = (locationId: string) => `/locations/${locationId}/media`;

export const mediaApi = {
  list: (locationId: string, purpose?: MediaPurpose) =>
    apiClient
      .get<ApiResponse<MediaAsset[]>>(base(locationId), { params: purpose ? { purpose } : undefined })
      .then((r) => r.data),

  requestUpload: (
    locationId: string,
    payload: { filename: string; contentType: AllowedImageMimeType; sizeBytes: number; purpose: MediaPurpose },
  ) =>
    apiClient
      .post<ApiResponse<RequestUploadResponse>>(`${base(locationId)}/upload-url`, payload)
      .then((r) => r.data),

  confirm: (locationId: string, mediaAssetId: string) =>
    apiClient
      .post<ApiResponse<MediaAsset>>(`${base(locationId)}/${mediaAssetId}/confirm`)
      .then((r) => r.data),

  remove: (locationId: string, mediaAssetId: string) =>
    apiClient.delete<ApiResponse<null>>(`${base(locationId)}/${mediaAssetId}`).then((r) => r.data),
};

/**
 * Reads dimensions from a File via the browser's Image element.
 * Lightweight client-side validation; backend re-checks with sharp on confirm.
 */
export function readImageDimensions(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const result = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(result);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image dimensions'));
    };
    img.src = url;
  });
}

/**
 * Full upload flow: request presigned URL, PUT to R2, then confirm.
 * Returns the finalized MediaAsset (status === 'ready').
 */
export async function uploadFile(params: {
  locationId: string;
  file: File;
  purpose: MediaPurpose;
}): Promise<MediaAsset> {
  const { locationId, file, purpose } = params;
  if (!ALLOWED_IMAGE_MIME_TYPES.includes(file.type as AllowedImageMimeType)) {
    throw new Error(`Unsupported file type: ${file.type}. Use JPG, PNG, or WebP.`);
  }

  const { data: { uploadUrl, asset } } = await mediaApi.requestUpload(locationId, {
    filename: file.name,
    contentType: file.type as AllowedImageMimeType,
    sizeBytes: file.size,
    purpose,
  });

  const putRes = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed: ${putRes.status} ${putRes.statusText}`);
  }

  const { data: confirmed } = await mediaApi.confirm(locationId, asset.id);
  return confirmed;
}
